import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
} from '@mui/material';

const UpdatePrint = ({ bomData = [] }) => {
    if (bomData.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="h6">No data to print</Typography>
            </Box>
        );
    }

    const firstItem = bomData[0];

    // Collect quantity columns
    const eqColumns = [];
    Object.keys(firstItem).forEach((key) => {
        if (key.startsWith('EQNm') && firstItem[key]?.trim()) {
            const num = Number(key.replace('EQNm', ''));
            eqColumns.push({
                index: num,
                label: firstItem[key].trim(),
                valueKey: `EQ${num}`,
            });
        }
    });

    eqColumns.sort((a, b) => a.index - b.index);

    // Split into two halves
    const midIndex = Math.ceil(eqColumns.length / 2);
    const leftColumns = eqColumns.slice(0, midIndex);
    const rightColumns = eqColumns.slice(midIndex);

    const renderTablePart = (columns) => {
        if (columns.length === 0) return null;

        return (
            <TableContainer
                component={Paper}
                elevation={1}
                sx={{
                    mb: 3,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                <Table size="small" aria-label="quantity table">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#e8f0fe' }}>
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    py: 1,
                                    px: 1,
                                    minWidth: 140,
                                    fontSize: '0.9rem',
                                }}
                            >
                                Ingredient
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    fontWeight: 600,
                                    py: 1,
                                    px: 1,
                                    minWidth: 60,
                                    fontSize: '0.9rem',
                                }}
                            >
                                Unit
                            </TableCell>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.valueKey}
                                    align="center"
                                    sx={{
                                        fontWeight: 600,
                                        py: 1,
                                        px: 1,
                                        minWidth: 55,
                                        fontSize: '0.85rem',
                                        borderLeft: '1px solid #d0e0ff',
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* Reference quantities row */}
                        {/* <TableRow sx={{ bgcolor: '#f5faff' }}>
                            <TableCell colSpan={2} sx={{ py: 0.5, px: 1.5 }} />
                            {columns.map((col) => (
                                <TableCell
                                    key={col.valueKey}
                                    align="center"
                                    sx={{
                                        py: 0.5,
                                        px: 1,
                                        fontSize: '0.85rem',
                                        color: 'primary.main',
                                        fontWeight: 500,
                                    }}
                                >
                                    {Number(firstItem[col.valueKey])?.toFixed(1) || '—'}
                                </TableCell>
                            ))}
                        </TableRow> */}
                    </TableHead>

                    <TableBody>
                        {bomData.map((row, idx) => (
                            <TableRow
                                key={idx}
                                sx={{
                                    '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                                    height: 32,
                                }}
                            >
                                <TableCell sx={{ py: 0.75, px: 1.5, fontSize: '0.88rem' }}>
                                    {row.ItmName?.trim() || '—'}
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.75, px: 1, fontSize: '0.88rem' }}>
                                    {row.UnitName || '—'}
                                </TableCell>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.valueKey}
                                        align="center"
                                        sx={{ py: 0.75, px: 1, fontSize: '0.88rem' }}
                                    >
                                        {Number(row[col.valueKey])?.toFixed(0) || '—'}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                p: 3,
                maxWidth: '210mm',
                mx: 'auto',
                bgcolor: 'white',
                fontFamily: 'Roboto, Arial, sans-serif',
            }}
        >
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ mb: 0.5, color: '#635bff' }}
                >
                    {firstItem.PropName?.trim() || 'No Property'}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                    1A/4, near Ishan Ayurvedic Medical College and Research Centre, Knowledge Park I, Greater Noida, Uttar Pradesh 201310
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                    Email: sales@clanbridge.com | Phone: 099533 98502
                </Typography>

                <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="primary.dark"
                    sx={{ mb: 0.5 }}
                >
                    {firstItem.FoodName?.trim() || 'Recipe'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    FoodBomId: {firstItem.FoodBomId || '—'} • Printed: {new Date().toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                    })}
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {renderTablePart(leftColumns)}
            {renderTablePart(rightColumns)}

            <Box sx={{ mt: 'auto', textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem' }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Thank you for choosing Clanbridge Living Pvt Ltd
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                    © 2024-{new Date().getFullYear()} Clanbridge Living Pvt Ltd. All Rights Reserved.
                </Typography>
            </Box>
        </Box>
    );
};

export default UpdatePrint;